import { GQL } from '@aline/graphql';
import merge from 'lodash/merge';
import { keys } from 'ramda';
import type { NodeEntry } from 'slate';
import { Editor, Transforms } from 'slate';

import type { Length } from '../../types';
import type { UUID } from '../../types/type-helpers';
import { convertUnit, inToPx, mapObjectValues } from '../../util';
import { getIsWorkflowSentForSignature } from '../../workflow-signing-state';
import type {
  AlineDescendant,
  DocProps,
  DocumentElement,
  GlobalStylesMap,
  ListDefinition,
  ListDefinitions,
  PageDimensions,
  PageNumberingConfig,
  ParagraphElement,
  ParagraphProps,
  RMDocProps,
} from '../internal';
import {
  createDefaultDocElement,
  defaultPageDimensions,
  defaultStyles,
  isDocumentElement,
  isPageDimensionsEdit,
  isPageFormatEdit,
  isParagraphElement,
  isStyleSheetEdit,
  minimumPageContentHeightIn,
  Queries,
} from '../internal';

export interface ListItemDetails {
  activeListProps: NonNullable<ParagraphElement['listProps']>;
  listDefinition: ListDefinition;
}
interface RecipientSubset extends Pick<GQL.WorkflowRecipient, 'status' | '__typename' | 'type'> {
  assignee: Pick<GQL.User, 'id' | '__typename'> | null;
}
export interface EnvelopeSubset extends Pick<GQL.Envelope, '__typename' | 'variant'> {
  recipients: RecipientSubset[];
}
export interface LoadedDocument extends Pick<GQL.Document, 'isSigned' | '__typename'> {
  isCertificate?: boolean;
}

export const documentQueries = {
  /**
   * Returns the active default tab stop in inches.
   *
   * NOTE: We don't take into account suggestions because Word does not support
   * suggestions for default tab stops.
   */
  activeDefaultTabStopIn: (docProps: DocProps | RMDocProps): number => {
    return docProps.defaultTabStopIn ?? 0.5;
  },

  activeDefaultTabStopPx: (docProps: DocProps | RMDocProps): number => {
    return inToPx(Queries.activeDefaultTabStopIn(docProps));
  },

  /**
   * Returns the active headerFooter properties, taking into account suggested edits.
   */
  activeHeaderOrFooterProps: (docProps: DocProps | RMDocProps): DocumentElement['headerFooter'] => {
    const pageFormatEdit = docProps.suggestedEdits?.find(isPageFormatEdit);
    if (pageFormatEdit)
      return merge({}, docProps.headerFooter, pageFormatEdit.value.headerFooter ?? {});

    return docProps.headerFooter ?? createDefaultDocElement().headerFooter;
  },

  /**
   * Return the document's active page dimensions, taking into account suggested edits.
   */
  activePageDimensions: (
    editorOrDocProps: Editor | DocProps | RMDocProps,
    unit: Length.Unit
  ): PageDimensions => {
    const [docProps] = Editor.isEditor(editorOrDocProps)
      ? Queries.getDocument(editorOrDocProps)
      : [editorOrDocProps];

    const basePageDimensions = docProps.pageDimensions ?? defaultPageDimensions;
    const pageDimensionsEdit = docProps.suggestedEdits?.find(isPageDimensionsEdit);

    const pageDimensions = pageDimensionsEdit
      ? merge({}, basePageDimensions, pageDimensionsEdit.value.pageDimensions ?? {})
      : basePageDimensions;

    return mapObjectValues(pageDimensions, dimensionIn => convertUnit(dimensionIn, 'in', unit));
  },

  /**
   * Returns the active style sheet for the document, taking into account suggested edits.
   */
  activeStyleSheet: (editor: Editor): GlobalStylesMap => {
    const [document] = Queries.getDocument(editor);
    const docStyleSheet = document.styleSheet ?? defaultStyles;
    const styleSheetEdit = document.suggestedEdits?.find(isStyleSheetEdit);
    if (styleSheetEdit) return merge({}, docStyleSheet, styleSheetEdit.value.styleSheet);

    return docStyleSheet;
  },

  /**
   * Creates state that we attach to the Editor about a given user's authorization so that Slate API transforms
   * know how to behave.
   */
  createEditorUserState(
    editor: Editor,
    options: {
      currentUserId: UUID | null;
      document: LoadedDocument;
      workflow: Pick<GQL.Workflow, 'state'> | null;
      envelope?: EnvelopeSubset | null;
    }
  ): NonNullable<Editor['userState']> {
    const { currentUserId, document, envelope = null, workflow } = options;
    const isSigned = document.isSigned;
    const isCertificate = document.isCertificate ?? false;
    const isPartOfCreatedDSEnvelope = envelope?.variant === GQL.EnvelopeVariant.DocuSign;
    const isInSigningCeremony = workflow?.state === GQL.WorkflowState.Signing;
    const signers =
      envelope?.recipients.filter(
        recipient => recipient.type === GQL.WorkflowRecipientType.Signer
      ) ?? [];
    const userSigners = signers.filter(
      signer => signer.assignee && signer.assignee.id === currentUserId
    );
    const userSigned =
      !!userSigners.length &&
      userSigners.every(signer => signer.status === GQL.WorkflowRecipientStatus.Completed);
    const userDeclinedSigning =
      !!userSigners.length &&
      userSigners.some(signer => signer.status === GQL.WorkflowRecipientStatus.Declined);

    return {
      ...editor.userState,
      editingUserId: currentUserId,
      isInSigningCeremony,
      isReadOnly: isCertificate || userSigned || isSigned || isPartOfCreatedDSEnvelope,
      isSigned,
      userDeclinedSigning,
      wasSentForSignature: getIsWorkflowSentForSignature(workflow),
    };
  },

  /**
   * Determine whether a document has unresolved tracked changes.
   */
  docHasUnresolvedTrackedChanges: (editor: Editor): boolean => {
    const nodesWithUnresolvedEdits = Editor.nodes<AlineDescendant>(editor, {
      at: [],
      match: node =>
        !Editor.isEditor(node) &&
        ((Array.isArray(node.suggestedEdits) && !!node.suggestedEdits?.length) ||
          // Paragraph split/merge suggestions live on glyphProps, not node.suggestedEdits.
          (isParagraphElement(node) && !!node.glyphProps?.suggestedEdits?.length)),
      voids: true,
    });
    /**
     * Use .next().value since Editor.nodes returns a Generator and its way faster to simply check the first node vs.
     * iterating over all nodes or spreading to an array and using .some, etc.
     */
    const firstMatch = nodesWithUnresolvedEdits.next().value;

    return !!firstMatch;
  },

  getActivePageNumbering: (docProps: DocProps | RMDocProps): PageNumberingConfig => {
    const docPageNumbering: PageNumberingConfig = docProps.headerFooter.pageNumbering ?? {
      format: 'decimal',
      start: 1,
    };
    const pageFormatEdit = docProps.suggestedEdits?.find(isPageFormatEdit);
    if (pageFormatEdit)
      return merge({}, docPageNumbering, pageFormatEdit.value.headerFooter?.pageNumbering ?? {});

    return docPageNumbering;
  },

  /**
   * Return the document.
   */
  getDocument: (editor: Editor): NodeEntry<DocumentElement> => {
    const documentElementIndex = editor.children.findIndex(isDocumentElement);
    if (documentElementIndex === -1) {
      const documentElement = createDefaultDocElement();

      Transforms.insertNodes(editor, documentElement, { at: [0] });

      return [documentElement, [0]];
    }

    const documentElement = editor.children[documentElementIndex] as DocumentElement;

    return [documentElement, [documentElementIndex]];
  },

  /**
   * return array of nodes that are style heading elements
   */
  getHeadings: (editor: Editor): NodeEntry<ParagraphElement>[] => {
    const nodes = Editor.nodes<ParagraphElement>(editor, {
      at: [],
      match: node => isParagraphElement(node) && Queries.isHeading(node),
    });

    return [...nodes];
  },

  /**
   * Get a particular ListDefinition by listId.
   */
  getListDefinition: (editor: Editor, listDefinitionId: UUID): ListDefinition | null => {
    const { listDefinitions } = Queries.getNumbering(editor);

    return listDefinitions[listDefinitionId] ?? null;
  },

  /**
   * Get the list details for a given paragraph if the paragraph is a list item.
   */
  getListItemDetails: (editor: Editor, paragraph: ParagraphProps): ListItemDetails | null => {
    const activeListProps = Queries.renderedParagraphListProps(paragraph);
    if (!activeListProps) return null;

    const { listDefinitions } = Queries.getNumbering(editor);
    const listDefinition = listDefinitions[activeListProps.listId];
    if (!listDefinition) return null;

    return {
      activeListProps,
      listDefinition,
    };
  },

  /**
   * Return the document.
   */
  getMaybeDocument: (editor: Editor): NodeEntry<DocumentElement> | null => {
    const documentElementIndex = editor.children.findIndex(isDocumentElement);
    if (documentElementIndex === -1) return null;

    const documentElement = editor.children[documentElementIndex];
    if (!isDocumentElement(documentElement)) return null;

    return [documentElement, [documentElementIndex]];
  },

  /**
   * Get numbering info for the document.
   */
  getNumbering: (
    editor: Editor
  ): {
    documentEntry: NodeEntry<DocumentElement>;
    listDefinitions: ListDefinitions;
  } => {
    const documentEntry = Queries.getDocument(editor);
    const [document] = documentEntry;

    return {
      documentEntry,
      listDefinitions: document.listDefinitions ?? {},
    };
  },

  isHeading(node: ParagraphElement) {
    return node.variant.startsWith('style-heading');
  },

  /**
   * Returns the document's page dimensions.
   */
  pageDimensions: (editor: Editor): PageDimensions => {
    const [document] = Queries.getDocument(editor);

    return document.pageDimensions ?? defaultPageDimensions;
  },

  pageDimensionsValid: (
    editor: Editor,
    partialPageDimensions: Partial<PageDimensions>
  ): boolean => {
    const pageDimensions = {
      ...Queries.activePageDimensions(editor, 'in'),
      ...partialPageDimensions,
    };
    const { height, marginBottom, marginLeft, marginRight, marginTop, width } = pageDimensions;

    if (keys(pageDimensions).some(key => pageDimensions[key] < 0)) return false;
    if (width - marginLeft - marginRight < 1) return false;
    return height - marginTop - marginBottom >= minimumPageContentHeightIn;
  },

  /**
   * Returns the document's stylesheet.
   */
  styleSheet: (editor: Editor): GlobalStylesMap => {
    const [document] = Queries.getDocument(editor);

    return document.styleSheet ?? defaultStyles;
  },
};
